/**
 * Modal detalhe Dose Certa — padrão Inventários (fieldset + ações + tabela de itens).
 */
import api from '../api.js';
import msgbox, { notify, ask } from '../../versa/msgbox.js';
import dialog from '../../versa/dialog.js';
import { labelEsteira, formatDateTimeBr, formatDateBr } from '../utils/dose-certa-labels.js';
import { openOutputsFiltered } from '../utils/navigate-to-outputs.js';
import { escapeHtml as esc } from '../utils/escape-html.js';
import { formatBiHttpError } from '../views/bi-http-error.js';
import * as types from '../types.js';

const STATUS_LABEL = {
	received: 'Na fila',
	processing: 'Processando',
	done: 'Integrado',
	error: 'Erro',
	rejected: 'Rejeitado',
};

const STATUS_BADGE = {
	received: 'badge-fila',
	processing: 'badge-counting',
	done: 'badge-ready',
	error: 'badge-cancelled',
	rejected: 'badge-closed',
};

export const DOSE_CERTA_HELP_HTML = `
<div class="dc-help-body">
<h4>O que é esta tela</h4>
<p>Central de controle do <strong>programa CEBAF (Dose Certa)</strong>. O TechHub faz a distribuição (rateio) fora do SIAL e envia os pedidos;
o SIAL <strong>integra automaticamente</strong> cada pacote em <strong>Pedido de Saída</strong>. Aqui o gestor comanda o fluxo:
<strong>Efetuar derrubada</strong> (gera a onda: saídas reservadas + descer no empilhador), <strong>Enviar ao sorter</strong> (esteira)
e <strong>Separação padrão</strong> (coletor, sem esteira).</p>

<h4>Coluna Status (pacote na fila)</h4>
<p>Situação do pacote <strong>dentro do SIAL</strong> (chegou do TechHub? já virou saída? deu erro?). Não é o status da saída nem da esteira.</p>
<ul>
<li><strong>Na fila</strong> — chegou do TechHub e ainda não foi integrado (a integração em Pedido de Saída é automática; se ficar em Erro, use Reintegrar).</li>
<li><strong>Processando</strong> — promoção em andamento (aguarde; não clique de novo).</li>
<li><strong>Integrado</strong> — Pedido de Saída já criado. A partir daqui: Efetuar derrubada → Enviar ao sorter ou Separação padrão.</li>
<li><strong>Erro</strong> — a integração falhou (SKU, lote, validade, estoque etc.). Corrija a causa e use <strong>Reintegrar</strong>.</li>
<li><strong>Rejeitado</strong> — pacote descartado na validação operacional (não segue para saída).</li>
</ul>

<h4>Coluna Esteira (equipamento BHT)</h4>
<p>Só faz sentido depois da <strong>derrubada</strong> e de <strong>Enviar ao sorter</strong>. Diz se o pedido já foi (ou não) para a esteira de separação.</p>
<ul>
<li><strong>Não</strong> — ainda não há saída na esteira (ex.: pacote só integrado/derrubado, ou em separação padrão/coletor).</li>
<li><strong>—</strong> — saída marcada para esteira, mas ainda sem status detalhado.</li>
<li><strong>Pendente</strong> — saída pronta para ir à esteira (ainda não enviada / aguardando envio).</li>
<li><strong>Enviado</strong> — onda já foi enviada à esteira BHT.</li>
<li><strong>Conferido</strong> — esteira concluiu a conferência do pedido.</li>
<li><strong>Erro</strong> — falha no envio ou na esteira; trate e use Reenviar à esteira se for o caso.</li>
<li><strong>Coletor</strong> — contingência: atendimento sem esteira (separação no coletor). Motivo fica na auditoria.</li>
</ul>
<p><strong>Resumo:</strong> Status = “o pacote do TechHub no SIAL”. Esteira = “a saída já está na esteira BHT?”.</p>

<h4>Fluxo Dose Certa (onda)</h4>
<ol>
<li>O TechHub envia os pedidos já rateados — o SIAL <strong>integra automaticamente</strong> em Pedido de Saída.</li>
<li>Selecione os pacotes na grade e use <strong>Efetuar derrubada</strong> — gera a onda (saídas reservadas + tarefas de descer).</li>
<li><strong>Enviar ao sorter</strong> — manda a saída à esteira BHT (terciária + fracionado). Evolui: <strong>Pendente → Enviado → Conferido</strong>.</li>
<li><strong>Separação padrão</strong> — alternativa sem esteira: fila normal do coletor.</li>
</ol>
<h4>Observações</h4>
<ul>
<li><strong>Reintegrar pedido</strong> — reprocessa um pacote que ficou em <strong>Erro</strong> na integração.</li>
<li><strong>Reenviar à esteira</strong> — reenvia uma onda já ENVIADA (só após confirmar na BHT que a anterior foi encerrada).</li>
</ul>
<h4>Como usar a grade</h4>
<ul>
<li><strong>Filtrar</strong> — empresa (obrigatória), programa, status, pedido, fatura, rota, destinatário, local, saída e esteira.</li>
<li><strong>Clique na linha</strong> — abre o detalhe do pacote.</li>
<li><strong>Link da Saída</strong> — abre a tela <strong>Saídas</strong> já filtrada naquela saída.</li>
</ul>
</div>`;

function openHelp()
{
	let mb;
	mb = msgbox({
		title: 'Ajuda — Dose Certa (SESSP)',
		text: DOSE_CERTA_HELP_HTML,
		buttons: [{ label: 'Fechar', cls: 'bn_Enter' }],
		callback: () => mb.destroy(),
	});
}

async function readJson(res)
{
	const text = await res.text();
	if (!res.ok) throw new Error(formatBiHttpError(res.status, text));
	try {
		return JSON.parse(text);
	} catch {
		throw new Error('Resposta inválida da API.');
	}
}

function askYes(text, title)
{
	return new Promise((resolve) => {
		ask(text, title, (key) => resolve(key === 'Y'));
	});
}

function askMotivo(defaultText)
{
	const padrao = defaultText || 'Esteira em manutenção';
	return new Promise((resolve) => {
		const html = `
			<div class="msgbox">
				<div class="mb-title">Motivo</div>
				<div class="mb-text">
					<p class="mts">Informe o motivo (fica na auditoria):</p>
					<label class="mts">Motivo
						<input type="text" id="dc-motivo-input" style="width:100%;min-width:18rem;" value="${esc(padrao)}">
					</label>
				</div>
				<button type="button" class="button mtl bn_Escape">Cancelar</button>
				<button type="button" class="button mtl bn_Enter">Confirmar</button>
			</div>
		`;
		let dlg;
		dlg = dialog.create(html, (key) => {
			if (key === 'Enter') {
				const inp = document.getElementById('dc-motivo-input');
				resolve((inp?.value || '').trim() || padrao);
			} else {
				resolve(null);
			}
			dlg.destroy();
		}, /Enter|Escape/);
	});
}

function _badgeClass(st)
{
	return STATUS_BADGE[st] || 'badge-closed';
}

function _renderItems(itens)
{
	const list = Array.isArray(itens) ? itens : [];
	if (!list.length)
		return '<p style="color:#999;font-size:0.8rem;">Sem itens neste pacote.</p>';
	const rows = list.map((it) => `
		<tr>
			<td class="dc-col-num">${it.sq_order ?? ''}</td>
			<td>${esc(it.item_deposit || '')}</td>
			<td>${esc(it.lote || '')}</td>
				<td>${esc(formatDateBr(it.dat_hor_validade))}</td>
			<td class="dc-col-num">${it.qtd_item_atendido ?? ''}</td>
			<td>${esc(it.tip_estoque || '')}</td>
		</tr>
	`).join('');
	return `
		<div id="dc-items-table-container">
			<table>
				<thead>
					<tr>
						<th>#</th><th>SKU</th><th>Lote</th><th>Validade</th><th>Qtd</th><th>Tip. estoque</th>
					</tr>
				</thead>
				<tbody>${rows}</tbody>
			</table>
		</div>
	`;
}

function _showDetail(root, pacote)
{
	const p = pacote || {};
	const st = p.status || '';
	const pct = Math.max(0, Math.min(100, Number(p.progress?.percent) || 0));
	const badge = root.querySelector('#dc-status-badge');
	if (badge) {
		badge.className = `badge ${_badgeClass(st)}`;
		badge.textContent = STATUS_LABEL[st] || st || '—';
	}
	const progLabel = root.querySelector('#dc-progress-label');
	if (progLabel)
		progLabel.textContent = p.progress?.label || '';
	const fill = root.querySelector('#dc-progress-fill');
	if (fill) fill.style.width = `${pct}%`;

	// DC-06: progresso físico real (separação) só quando há dados confiáveis.
	const fisWrap = root.querySelector('#dc-progress-fisico-wrap');
	const fisLabel = root.querySelector('#dc-progress-fisico-label');
	const fisFill = root.querySelector('#dc-progress-fisico-fill');
	const fisico = p.progress_fisico;
	if (fisWrap) {
		if (fisico && Number.isFinite(Number(fisico.percent))) {
			const fpct = Math.max(0, Math.min(100, Number(fisico.percent) || 0));
			fisWrap.hidden = false;
			if (fisLabel) fisLabel.textContent = `Separação física: ${fpct}% — ${fisico.label || ''}`;
			if (fisFill) fisFill.style.width = `${fpct}%`;
		} else {
			fisWrap.hidden = true;
		}
	}

	const setTxt = (sel, v) => {
		const el = root.querySelector(sel);
		if (el) el.textContent = v == null || v === '' ? '—' : String(v);
	};
	const programa = p.programa_nome
		? (p.programa && String(p.programa_nome) !== String(p.programa)
			? `${p.programa} — ${p.programa_nome}`
			: p.programa_nome)
		: (p.programa || '—');
	setTxt('#dc-meta-pedido', p.numero_pedido);
	setTxt('#dc-meta-fatura', p.numero_fatura);
	setTxt('#dc-meta-local', p.codigo_local);
	setTxt('#dc-meta-programa', programa);
	setTxt('#dc-meta-rota', p.rota || '—');
	setTxt('#dc-meta-destinatario', p.destinatario || '—');
	setTxt('#dc-meta-pedido-sial', p.output_order_id || '—');
	setTxt('#dc-meta-sorter', labelEsteira(p));
	setTxt('#dc-meta-itens', p.item_count ?? (p.itens || []).length);
	setTxt('#dc-meta-recebido', formatDateTimeBr(p.received_at));

	const saidaEl = root.querySelector('#dc-meta-saida');
	if (saidaEl) {
		if (p.output_id && p.company_id) {
			saidaEl.innerHTML = `<a href="#outputs" class="dc-open-output" data-company="${esc(p.company_id)}" data-output="${esc(p.output_id)}" title="Abrir Saídas filtrada nesta saída">#${esc(p.output_id)}</a>`;
		} else {
			saidaEl.textContent = '—';
		}
	}

	const ondaEl = root.querySelector('#dc-meta-onda');
	if (ondaEl) {
		if (p.wave_id && p.company_id) {
			ondaEl.innerHTML = `<a href="#picking-waves" class="dc-open-wave" data-company="${esc(p.company_id)}" data-wave="${esc(p.wave_id)}" title="Abrir Derrubada — ondas filtrada nesta onda">#${esc(p.wave_id)}</a>`;
		} else {
			ondaEl.textContent = '—';
		}
	}

	const errEl = root.querySelector('#dc-last-error');
	if (errEl) {
		if (p.last_error) {
			errEl.hidden = false;
			errEl.textContent = p.last_error;
		} else {
			errEl.hidden = true;
			errEl.textContent = '';
		}
	}
	const excEl = root.querySelector('#dc-exception-reason');
	if (excEl) {
		if (p.exception_reason) {
			excEl.hidden = false;
			excEl.textContent = `Exceção: ${p.exception_reason}`;
		} else {
			excEl.hidden = true;
			excEl.textContent = '';
		}
	}

	const itemsHost = root.querySelector('#dc-items-panel');
	if (itemsHost)
		itemsHost.innerHTML = _renderItems(p.itens);

	const sorterStatus = String(p.sorter_status || '').toUpperCase();
	const hasOutput = Boolean(p.output_id);
	const canIntegrar = st === 'error' || (st === 'received' && !p.output_order_id);
	const canDerrubada = Boolean(p.output_order_id) && !hasOutput;
	const canSendSorter = hasOutput && sorterStatus !== 'SENT' && sorterStatus !== 'CHECKED';
	const canSeparacao = hasOutput && !p.exception_manual;
	const canResend = hasOutput && sorterStatus === 'SENT';
	const show = (sel, on) => {
		const el = root.querySelector(sel);
		if (el) el.style.display = on ? '' : 'none';
	};
	show('#bn-dc-integrar', canIntegrar);
	show('#bn-dc-derrubada', canDerrubada);
	show('#bn-dc-send-sorter', canSendSorter);
	show('#bn-dc-separacao', canSeparacao);
	show('#bn-dc-resend', canResend);
	const resendBtn = root.querySelector('#bn-dc-resend');
	if (resendBtn)
		resendBtn.textContent = p.exception_manual ? 'Voltar à esteira' : 'Reenviar à esteira';

	root.dataset.companyId = p.company_id || root.dataset.companyId || '';
	root.dataset.inboxId = p.id || '';
	root._pacote = p;
}

async function _reloadDetail(root, crudReload)
{
	const companyId = root.dataset.companyId;
	const inboxId = root.dataset.inboxId;
	if (!companyId || !inboxId) return;
	try {
		const data = await readJson(await api.especial_sessp_dose_certa_detail(companyId, inboxId));
		const p = { ...(data.pacote || {}), company_id: companyId };
		_showDetail(root, p);
		if (typeof crudReload === 'function')
			crudReload();
	} catch (err) {
		notify(err.message || err, 'Dose Certa');
	}
}

function _bindActions(root, formset)
{
	if (root.dataset.dcBound === '1') return;
	root.dataset.dcBound = '1';

	const crudReload = () => {
		try {
			root._dcCrud?.reloadPage?.();
		} catch { /* ignore */ }
	};

	// DC-09: trava anti-duplo-clique. Desabilita as ações durante um request e
	// mostra feedback no botão acionado.
	const ACTION_SELECTORS = ['#bn-dc-integrar', '#bn-dc-derrubada', '#bn-dc-send-sorter', '#bn-dc-separacao', '#bn-dc-resend', '#bn-dc-reload'];
	const setActionsDisabled = (disabled) => {
		ACTION_SELECTORS.forEach((sel) => {
			const el = root.querySelector(sel);
			if (el) el.disabled = disabled;
		});
	};
	const withBusy = async (btn, fn) => {
		if (root.dataset.dcBusy === '1') return;
		root.dataset.dcBusy = '1';
		const label = btn ? btn.textContent : '';
		if (btn) btn.textContent = 'Processando…';
		setActionsDisabled(true);
		try {
			await fn();
		} finally {
			setActionsDisabled(false);
			if (btn) btn.textContent = label;
			root.dataset.dcBusy = '0';
		}
	};

	root.querySelector('#dc-help')?.addEventListener('click', (ev) => {
		ev.preventDefault();
		openHelp();
	});

	root.addEventListener('click', (ev) => {
		const a = ev.target?.closest?.('a.dc-open-output');
		if (!a || !root.contains(a)) return;
		ev.preventDefault();
		ev.stopPropagation();
		const companyId = a.getAttribute('data-company');
		const outputId = a.getAttribute('data-output');
		if (!companyId || !outputId) return;
		openOutputsFiltered({ companyId, outputId });
	});

	root.querySelector('#bn-dc-integrar')?.addEventListener('click', (ev) => withBusy(ev.currentTarget, async () => {
		const companyId = root.dataset.companyId;
		const inboxId = root.dataset.inboxId;
		if (!await askYes('Reintegrar o pacote (criar o Pedido de Saída)?', 'Dose Certa')) return;
		try {
			const data = await readJson(await api.especial_sessp_dose_certa_integrar(companyId, inboxId, {}));
			notify(
				data.already ? 'Pacote já integrado.' : `Pedido de Saída #${data.output_order_id} criado.`,
				'Dose Certa',
			);
			await _reloadDetail(root, crudReload);
		} catch (err) {
			notify(err.message || err, 'Dose Certa');
			await _reloadDetail(root, crudReload);
		}
	}));

	root.querySelector('#bn-dc-derrubada')?.addEventListener('click', (ev) => withBusy(ev.currentTarget, async () => {
		const companyId = root.dataset.companyId;
		const inboxId = root.dataset.inboxId;
		if (!await askYes('Efetuar a derrubada (gerar a onda) deste pacote?', 'Dose Certa — derrubada')) return;
		try {
			const data = await readJson(await api.especial_sessp_dose_certa_derrubada(companyId, { inbox_ids: [inboxId] }));
			const vinc = (data.vinculados || [])[0];
			notify(
				vinc ? `Onda ${data.wave_id} — saída #${vinc.output_id} reservada.` : `Onda ${data.wave_id} gerada.`,
				'Dose Certa — derrubada',
			);
			await _reloadDetail(root, crudReload);
		} catch (err) {
			notify(err.message || err, 'Dose Certa — derrubada');
			await _reloadDetail(root, crudReload);
		}
	}));

	root.querySelector('#bn-dc-send-sorter')?.addEventListener('click', (ev) => withBusy(ev.currentTarget, async () => {
		const companyId = root.dataset.companyId;
		const inboxId = root.dataset.inboxId;
		if (!await askYes('Enviar a saída deste pacote ao sorter/esteira?', 'Dose Certa — sorter')) return;
		try {
			const data = await readJson(await api.especial_sessp_dose_certa_enviar_sorter(companyId, inboxId, {}));
			const sendMsg = data.sorter_send && data.sorter_send.ok === false
				? ' (onda pendente de envio — verifique o status)'
				: '';
			notify(`Saída #${data.output_id} enviada ao sorter.${sendMsg}`, 'Dose Certa — sorter');
			await _reloadDetail(root, crudReload);
		} catch (err) {
			notify(err.message || err, 'Dose Certa — sorter');
			await _reloadDetail(root, crudReload);
		}
	}));

	root.querySelector('#bn-dc-separacao')?.addEventListener('click', (ev) => withBusy(ev.currentTarget, async () => {
		const companyId = root.dataset.companyId;
		const inboxId = root.dataset.inboxId;
		const motivo = await askMotivo('Separação padrão (coletor)');
		if (motivo === null) return;
		try {
			const data = await readJson(await api.especial_sessp_dose_certa_separacao_padrao(companyId, inboxId, { motivo }));
			notify(data.nota || 'Enviado para separação padrão (coletor).', 'Dose Certa — separação padrão');
			if (data.sent_warning)
				notify(data.sent_warning, 'Dose Certa — atenção');
			await _reloadDetail(root, crudReload);
		} catch (err) {
			notify(err.message || err, 'Dose Certa — separação padrão');
		}
	}));

	root.querySelector('#bn-dc-resend')?.addEventListener('click', (ev) => withBusy(ev.currentTarget, async () => {
		const companyId = root.dataset.companyId;
		const inboxId = root.dataset.inboxId;
		if (!await askYes('Reenviar onda à esteira BHT?', 'Dose Certa')) return;
		try {
			const data = await readJson(await api.especial_sessp_dose_certa_sorter_resend(companyId, inboxId));
			const send = data.resend?.send || {};
			notify(
				send.ok ? 'Reenvio OK.' : (send.reason || send.error || 'Reenvio solicitado — verifique status.'),
				'Dose Certa',
			);
			await _reloadDetail(root, crudReload);
		} catch (err) {
			// DC-03: 409 quando a onda já foi enviada à BHT — pede confirmação explícita
			// de que a onda anterior foi cancelada/encerrada antes de reenviar.
			const msg = String(err.message || err || '');
			if (/j[áa] consta como ENVIADA|409/i.test(msg)) {
				if (await askYes(`${msg}\n\nConfirmou na BHT que a onda anterior foi cancelada/encerrada? Reenviar mesmo assim?`, 'Dose Certa — reenvio')) {
					try {
						const data2 = await readJson(await api.especial_sessp_dose_certa_sorter_resend(companyId, inboxId, { confirm: true }));
						const send2 = data2.resend?.send || {};
						notify(send2.ok ? 'Reenvio OK.' : (send2.reason || send2.error || 'Reenvio solicitado.'), 'Dose Certa');
					} catch (err2) {
						notify(err2.message || err2, 'Dose Certa');
					}
				}
			} else {
				notify(msg, 'Dose Certa');
			}
			await _reloadDetail(root, crudReload);
		}
	}));

	root.querySelector('#bn-dc-reload')?.addEventListener('click', (ev) => withBusy(ev.currentTarget, () => _reloadDetail(root, crudReload)));
}

export { openHelp };

export default {
	html: `
		<form id="dc-session" class="form sial-modal-form" autocomplete="off">
			<style>
				#dc-session.form.sial-modal-form {
					background-color: var(--color-box, #fff);
					box-sizing: border-box;
				}
				#dc-session { max-width: 56rem; }
				#dc-session fieldset { border: 1px solid #ddd; border-radius: 6px; padding: 10px 14px; margin-bottom: 10px; }
				#dc-session legend { font-weight: 600; color: #1e3a5f; padding: 0 6px; }
				#dc-session .field-row { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-start; margin-bottom: 8px; }
				#dc-session .dc-head {
					display: flex; align-items: flex-start; justify-content: space-between;
					gap: 0.75rem; margin-bottom: 0.5rem;
				}
				#dc-session .dc-head h2 { margin: 0; font-size: 1.1rem; color: #1e3a5f; }
				#dc-session .dc-kicker { margin: 0 0 0.2rem; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #64748b; }
				#dc-session .dc-help {
					display: inline-flex; align-items: center; justify-content: center;
					width: 1.35rem; height: 1.35rem; padding: 0;
					border: 1px solid #9ca3af; border-radius: 999px;
					background: #f3f4f6; color: #374151;
					font-size: 0.85rem; font-weight: 700; line-height: 1; cursor: pointer;
				}
				#dc-session .dc-help:hover { background: #e5e7eb; }
				#dc-session .dc-help:focus { outline: 2px solid #2563eb; outline-offset: 2px; }
				#dc-session .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600; }
				.badge-fila { background: #dbeafe; color: #1e40af; }
				.badge-counting { background: #fef3c7; color: #92400e; }
				.badge-ready { background: #dcfce7; color: #166534; }
				.badge-closed { background: #f3f4f6; color: #6b7280; }
				.badge-cancelled { background: #fee2e2; color: #991b1b; }
				#dc-session .dc-meta-grid {
					display: grid; grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
					gap: 8px; margin: 8px 0;
				}
				#dc-session .dc-card {
					border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 10px; background: #f8fafc;
				}
				#dc-session .dc-card .label { color: #64748b; font-size: .72rem; }
				#dc-session .dc-card .value { font-size: 0.95rem; font-weight: 700; margin-top: 2px; word-break: break-word; }
				#dc-session .dc-progress-track { height: 10px; background: #e5e7eb; border-radius: 999px; overflow: hidden; margin: 6px 0 10px; }
				#dc-session .dc-progress-fill { height: 100%; background: #16a34a; width: 0%; transition: width .2s ease; }
				#dc-session .bn-action { padding: 5px 12px; border: none; border-radius: 4px; cursor: pointer; font-size: 0.82rem; font-weight: 600; }
				#dc-session .bn-primary { background: #1e3a5f; color: #fff; }
				#dc-session .bn-success { background: #16a34a; color: #fff; }
				#dc-session .bn-warn { background: #d97706; color: #fff; }
				#dc-items-table-container { max-width: 100%; overflow: auto; max-height: min(36vh, 20rem); border: 1px solid #e2e8f0; border-radius: 6px; }
				#dc-items-panel table { width: 100%; border-collapse: collapse; font-size: 0.78rem; }
				#dc-items-panel th { background: #1e3a5f; color: #fff; padding: 4px 6px; text-align: left; position: sticky; top: 0; }
				#dc-items-panel td { padding: 3px 6px; border-bottom: 1px solid #e5e7eb; }
				#dc-items-panel .dc-col-num { text-align: right; width: 3.5rem; }
				#dc-last-error, #dc-exception-reason { color: #b91c1c; font-size: 0.8rem; margin: 6px 0 0; }
				#dc-session a.dc-open-output { color:#1e3a5f; font-weight:700; text-decoration:underline; }
				#dc-session a.dc-open-output:hover { color:#2563eb; }
				.dc-help-body { max-height: 65vh; overflow-y: auto; text-align: left; }
				.dc-help-body h4 {
					margin: 0.85rem 0 0.35rem 0; font-size: 0.78rem; font-weight: 700;
					text-transform: uppercase; letter-spacing: 0.03em; color: #1f2937;
				}
				.dc-help-body h4:first-child { margin-top: 0; }
				.dc-help-body p, .dc-help-body li {
					margin: 0 0 0.35rem 0; font-size: 0.875rem; line-height: 1.45; color: #374151;
				}
				.dc-help-body ul, .dc-help-body ol { margin: 0.25rem 0 0.5rem 1.15rem; padding: 0; }
			</style>

			<div class="dc-head">
				<div>
					<p class="dc-kicker">SESSP — Dose Certa</p>
					<h2>Detalhe do pacote</h2>
				</div>
				<button type="button" class="dc-help" id="dc-help" title="Ajuda: o que é esta tela e como funciona" aria-label="Ajuda sobre Dose Certa">?</button>
			</div>

			<input type="hidden" id="id">
			<input type="hidden" id="company_id">

			<fieldset>
				<legend>Identificação</legend>
				<div style="display:flex; gap:8px; flex-wrap:wrap; align-items:center;">
					<span id="dc-status-badge" class="badge">—</span>
					<span id="dc-progress-label" style="font-size:0.8rem; color:#555;"></span>
				</div>
				<div class="dc-progress-track"><div id="dc-progress-fill" class="dc-progress-fill"></div></div>
				<div style="font-size:0.7rem; color:#94a3b8; margin:-4px 0 6px;">Etapa do processamento do pacote no SIAL — não é o total de unidades separadas.</div>
				<div id="dc-progress-fisico-wrap" hidden style="margin-bottom:8px;">
					<span id="dc-progress-fisico-label" style="font-size:0.78rem; color:#555;"></span>
					<div class="dc-progress-track"><div id="dc-progress-fisico-fill" class="dc-progress-fill" style="background:#2563eb;"></div></div>
					<div style="font-size:0.7rem; color:#94a3b8; margin-top:-4px;">Separação física da saída (unidades separadas ÷ requeridas).</div>
				</div>
				<div class="dc-meta-grid">
					<div class="dc-card"><div class="label">Pedido TechHub</div><div class="value" id="dc-meta-pedido">—</div></div>
					<div class="dc-card"><div class="label">Fatura TechHub</div><div class="value" id="dc-meta-fatura">—</div></div>
					<div class="dc-card"><div class="label">Programa</div><div class="value" id="dc-meta-programa">—</div></div>
					<div class="dc-card"><div class="label">Rota</div><div class="value" id="dc-meta-rota">—</div></div>
					<div class="dc-card"><div class="label">Destinatário</div><div class="value" id="dc-meta-destinatario">—</div></div>
					<div class="dc-card"><div class="label">Local</div><div class="value" id="dc-meta-local">—</div></div>
					<div class="dc-card"><div class="label">Recebido</div><div class="value" id="dc-meta-recebido">—</div></div>
					<div class="dc-card"><div class="label">Itens</div><div class="value" id="dc-meta-itens">—</div></div>
					<div class="dc-card"><div class="label">Pedido SIAL</div><div class="value" id="dc-meta-pedido-sial">—</div></div>
					<div class="dc-card"><div class="label">Onda</div><div class="value" id="dc-meta-onda">—</div></div>
					<div class="dc-card"><div class="label">Saída</div><div class="value" id="dc-meta-saida">—</div></div>
					<div class="dc-card"><div class="label">Esteira</div><div class="value" id="dc-meta-sorter">—</div></div>
				</div>
				<p id="dc-last-error" hidden></p>
				<p id="dc-exception-reason" hidden></p>
			</fieldset>

			<fieldset id="dc-actions-panel">
				<legend>Ações</legend>
				<div style="display:flex; gap:8px; flex-wrap:wrap; align-items:center;">
					<button type="button" id="bn-dc-integrar" class="bn-action bn-warn" style="display:none;">Reintegrar pedido</button>
					<button type="button" id="bn-dc-derrubada" class="bn-action bn-primary" style="display:none;">Efetuar derrubada</button>
					<button type="button" id="bn-dc-send-sorter" class="bn-action bn-primary" style="display:none;">Enviar ao sorter</button>
					<button type="button" id="bn-dc-separacao" class="bn-action bn-warn" style="display:none;">Separação padrão</button>
					<button type="button" id="bn-dc-resend" class="bn-action bn-success" style="display:none;">Reenviar à esteira</button>
					<button type="button" id="bn-dc-reload" class="bn-action bn-primary">Atualizar detalhe</button>
				</div>
			</fieldset>

			<fieldset>
				<legend>Itens do pacote</legend>
				<div id="dc-items-panel"><p style="color:#999; font-size:0.8rem;">Carregando itens...</p></div>
			</fieldset>

			<div style="display:flex; gap:8px; justify-content:flex-end; margin-top:10px;">
				<button type="button" class="bn_Escape button">Fechar</button>
			</div>
		</form>
	`,

	form: {
		selector: '#dc-session',
		init(formset) {
			Object.getPrototypeOf(this).init.call(this);
			const root = document.querySelector('#dc-session');
			if (root) {
				root._formset = formset;
				_bindActions(root, formset);
			}
		},
		afterEditSetValues(fs) {
			const vals = fs.form.getValues?.() || {};
			const root = document.querySelector('#dc-session');
			if (!root) return;
			root.dataset.companyId = vals.company_id || '';
			root.dataset.inboxId = vals.id || '';
			_showDetail(root, {
				id: vals.id,
				company_id: vals.company_id,
				numero_pedido: vals.numero_pedido,
				numero_fatura: vals.numero_fatura,
				status: vals.status,
				item_count: vals.item_count,
			});
			_reloadDetail(root);
		},
	},

	fields: [
		{ properties: { selector: '#id' }, types: [types.name()] },
		{ properties: { selector: '#company_id' }, types: [types.name()] },
	],
};
